import apiClient from '../client';
import type { GalleryAlbum, GalleryItem, GalleryVisibilityType } from '@/types';

const BASE = '/admin/gallery';

export interface GalleryItemPayload {
  url:           string;
  type:          string;
  thumbnailUrl?: string;
  fileId?:       string;
  mimeType?:     string;
}

export interface CreateAlbumPayload {
  name:                      string;
  parent_id?:                number | null;
  is_visible:                boolean;
  visibility_type?:          GalleryVisibilityType;
  visibility_class_ids?:     number[];
  visibility_admission_ids?: number[];
  category?:                 string;
  class_id?:                 number | null;
  class_name?:               string | null;
  items:                     GalleryItemPayload[];
}

export interface UpdateVisibilityPayload {
  visibility_type:           GalleryVisibilityType;
  visibility_class_ids?:     number[];
  visibility_admission_ids?: number[];
}

export interface UpdateAlbumPayload extends UpdateVisibilityPayload {
  name?:       string;
  is_visible?: boolean;
}

export const galleryApi = {
  // ── Album CRUD ──────────────────────────────────────────────────────────────
  /** Returns { data: GalleryAlbum[] }. Pass parent_id to fetch sub-albums. */
  listAlbums: (params?: Record<string, unknown>) =>
    apiClient
      .get<{ data: GalleryAlbum[] }>(`${BASE}/albums`, { params })
      .then((r) => r.data),

  /** Create album (with optional items). */
  createAlbum: (payload: CreateAlbumPayload) =>
    apiClient
      .post<{ data: GalleryAlbum }>(`${BASE}/albums`, payload)
      .then((r) => r.data.data),

  removeAlbum: (id: number) =>
    apiClient.delete(`${BASE}/albums/${id}`),

  updateAlbum: (id: number, payload: UpdateAlbumPayload) =>
    apiClient
      .put<{ data: GalleryAlbum }>(`${BASE}/albums/${id}`, payload)
      .then((r) => r.data.data),

  /** Update visibility type + class/student mappings for an album. */
  updateVisibility: (albumId: number, payload: UpdateVisibilityPayload) =>
    apiClient
      .put<{ data: GalleryAlbum }>(`${BASE}/albums/${albumId}/visibility`, payload)
      .then((r) => r.data.data),

  // ── Item operations inside an album ─────────────────────────────────────────
  listItems: (albumId: number, params?: Record<string, unknown>) =>
    apiClient
      .get<{ data: GalleryItem[] }>(`${BASE}/albums/${albumId}/items`, { params })
      .then((r) => r.data),

  addItems: (albumId: number, items: GalleryItemPayload[]) =>
    apiClient
      .post<{ data: GalleryItem[] }>(`${BASE}/albums/${albumId}/items`, { items })
      .then((r) => r.data.data),

  removeItem: (id: number) =>
    apiClient.delete(`${BASE}/items/${id}`),
};

